// The vertical scale of the accuracy chart (2026-10-08): fitted to the
// games' own distribution (`@chess/api-insights/fitted-scale`), so the dots
// spread over the plot whatever the player's level: the quantiles of the
// accuracies (5th, 25th, 50th, 75th, 95th) are pinned to fixed heights, 100
// to the top and 0 to the bottom, linear between. The tooltip keeps the
// exact numbers; the ticks, at the quantiles' values, show the scale, and
// the plot says so. Under `MIN_POINTS` games the quantiles mean little, so
// the scale is a log of the inaccuracy (100 − accuracy) instead, which
// stretches the top of the range.
import { fitKnots, knotTicks, positionOf } from '@chess/api-insights/fitted-scale';

export interface AccuracyScale {
  /** The share of the plot's height below an accuracy: 0 at 100, 1 at 0. */
  depth(accuracy: number): number;
  /** Tick values, top to bottom. */
  ticks: number[];
  /** Whether the scale was fitted to the games (false: the fallback log). */
  fitted: boolean;
}

/** Games needed to fit the scale to them. */
export const MIN_POINTS = 8;
/** The quantiles pinned, and the heights (shares from the top) they are pinned to. */
const QUANTILES = [0.95, 0.75, 0.5, 0.25, 0.05];
const HEIGHTS = [0.12, 0.32, 0.5, 0.68, 0.88];

const clamp = (a: number) => Math.max(0, Math.min(100, a));

/** The log fallback: a log of the inaccuracy. */
const LOG_C = 10;
const LOG_MAX = Math.log1p(100 / LOG_C);
export const logScale: AccuracyScale = {
  depth: (a) => Math.log1p((100 - clamp(a)) / LOG_C) / LOG_MAX,
  ticks: [100, 90, 80, 50, 0],
  fitted: false,
};

/** The scale for a set of accuracies: fitted to their quantiles when there are enough, else the log. */
export function accuracyScale(accuracies: readonly number[]): AccuracyScale {
  const values = accuracies.map(clamp);
  if (values.length < MIN_POINTS) return logScale;
  const knots = fitKnots(values, 100, 0, QUANTILES, HEIGHTS);
  return { depth: (a) => positionOf(knots, a), ticks: knotTicks(knots), fitted: true };
}
